import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { getLaporanKinerjaInstansi, getInstansiList, getTimezoneSettings, getLaporanIndividuInstansi, getKinerjaAggregateInstansi } from '../../lib/api';
import { getMonthName, getScoreColor, getScoreBadge } from '../../lib/utils';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { 
  ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, Download, FileText, Loader2, 
  TrendingUp, Award, Users, FileSpreadsheet, Landmark, UserCircle, Eye, Search, RefreshCw, Briefcase
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import * as XLSX from 'xlsx';
import { toast } from '../ui/sonner';

const ITEMS_PER_PAGE = 15;
const COLORS = ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444'];

const LaporanInstansiPage = () => {
  const { user, isAdminDinas } = useAuth();
  const navigate = useNavigate();
  const [laporan, setLaporan] = useState(null);
  const [kinerjaAggregate, setKinerjaAggregate] = useState(null);
  const [instansiList, setInstansiList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth() + 1);
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const [selectedInstansi, setSelectedInstansi] = useState('all');
  const [timezoneSettings, setTimezoneSettings] = useState({ timezone: 'WIT' });
  const [exporting, setExporting] = useState(false);
  const chartRef = useRef(null);
  
  // Pagination & Search state
  const [currentPage, setCurrentPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const searchTimeoutRef = useRef(null);

  // Detail dialog
  const [showDetailDialog, setShowDetailDialog] = useState(false);
  const [selectedPegawai, setSelectedPegawai] = useState(null);
  const [pegawaiDetail, setPegawaiDetail] = useState(null);
  const [loadingDetail, setLoadingDetail] = useState(false);

  // Debounce search
  const handleSearchChange = useCallback((value) => {
    setSearchQuery(value);
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }
    searchTimeoutRef.current = setTimeout(() => {
      setDebouncedSearch(value);
      setCurrentPage(1);
    }, 400);
  }, []);

  useEffect(() => {
    loadData();
  }, [selectedMonth, selectedYear, selectedInstansi, currentPage, debouncedSearch]);

  useEffect(() => {
    getTimezoneSettings().then(setTimezoneSettings).catch(console.error);
    if (isAdminDinas) {
      getInstansiList().then(setInstansiList).catch(console.error);
    }
  }, [isAdminDinas]);

  const loadData = async () => {
    try {
      setLoading(true);
      const offset = (currentPage - 1) * ITEMS_PER_PAGE;
      const instansiId = selectedInstansi === 'all' ? null : selectedInstansi;
      
      const [laporanData, aggregateData] = await Promise.all([
        getLaporanKinerjaInstansi(selectedMonth, selectedYear, instansiId, ITEMS_PER_PAGE, offset, debouncedSearch),
        getKinerjaAggregateInstansi(selectedMonth, selectedYear, instansiId)
      ]);
      
      setLaporan(laporanData);
      setKinerjaAggregate(aggregateData);
    } catch (error) {
      console.error('Failed to load laporan:', error);
      toast.error('Gagal memuat laporan');
    } finally {
      setLoading(false);
    }
  };

  const openDetailDialog = async (pegawai) => {
    setSelectedPegawai(pegawai);
    setShowDetailDialog(true);
    setLoadingDetail(true);
    
    try {
      const detail = await getLaporanIndividuInstansi(pegawai.pegawai_id, selectedMonth, selectedYear);
      setPegawaiDetail(detail);
    } catch (error) {
      console.error('Failed to load detail:', error);
      toast.error('Gagal memuat detail pegawai');
    } finally {
      setLoadingDetail(false);
    }
  };

  // Pagination
  const totalPages = laporan ? Math.ceil(laporan.pagination.total / ITEMS_PER_PAGE) : 1;
  
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  // Export functions
  const exportToPDF = async () => {
    setExporting(true);
    try {
      // Fetch all data for export
      const instansiId = selectedInstansi === 'all' ? null : selectedInstansi;
      const allData = await getLaporanKinerjaInstansi(selectedMonth, selectedYear, instansiId, null, 0, debouncedSearch);
      
      const doc = new jsPDF('l', 'mm', 'a4');
      const pageWidth = doc.internal.pageSize.getWidth();
      
      // Header
      doc.setFontSize(16);
      doc.text('LAPORAN KINERJA PEGAWAI INSTANSI', pageWidth / 2, 15, { align: 'center' });
      doc.setFontSize(11);
      doc.text(`Periode: ${getMonthName(selectedMonth)} ${selectedYear}`, pageWidth / 2, 22, { align: 'center' });
      
      const instansiNama = selectedInstansi === 'all' 
        ? 'Semua Instansi' 
        : instansiList.find(i => i.id === selectedInstansi)?.nama || '';
      doc.text(`Instansi: ${instansiNama}`, pageWidth / 2, 28, { align: 'center' });

      // Table data
      const tableData = allData.data.map((item, index) => [
        index + 1,
        item.pegawai_nama,
        item.pegawai_nip,
        item.instansi_nama,
        item.jabatan,
        item.total_hadir,
        item.hadir_tepat_waktu,
        item.hadir_terlambat,
        item.total_izin,
        item.total_mangkir,
        `${item.skor_akhir}%`
      ]);

      autoTable(doc, {
        head: [['No', 'Nama', 'NIP', 'Instansi', 'Jabatan', 'Hadir', 'Tepat', 'Terlambat', 'Izin', 'Mangkir', 'Skor']],
        body: tableData,
        startY: 35,
        styles: { fontSize: 8, cellPadding: 2 },
        headStyles: { fillColor: [79, 70, 229], textColor: 255 },
        alternateRowStyles: { fillColor: [245, 245, 250] }
      });

      // Footer
      const finalY = doc.lastAutoTable.finalY + 10;
      doc.setFontSize(9);
      doc.text(`Total Pegawai: ${allData.total_pegawai} | Hari Kerja: ${allData.total_hari_kerja}`, 14, finalY);
      doc.text(`Dicetak: ${new Date().toLocaleString('id-ID')}`, pageWidth - 14, finalY, { align: 'right' });

      doc.save(`Laporan_Instansi_${getMonthName(selectedMonth)}_${selectedYear}.pdf`);
      toast.success('PDF berhasil diunduh');
    } catch (error) {
      toast.error('Gagal mengekspor PDF');
    } finally {
      setExporting(false);
    }
  };

  const exportToExcel = async () => {
    setExporting(true);
    try {
      const instansiId = selectedInstansi === 'all' ? null : selectedInstansi;
      const allData = await getLaporanKinerjaInstansi(selectedMonth, selectedYear, instansiId, null, 0, debouncedSearch);

      const excelData = allData.data.map((item, index) => ({
        'No': index + 1,
        'Nama Pegawai': item.pegawai_nama,
        'NIP': item.pegawai_nip,
        'Instansi': item.instansi_nama,
        'Jabatan': item.jabatan,
        'Bidang': item.bidang,
        'Status Kepegawaian': item.status_kepegawaian,
        'Total Hari Kerja': item.total_hari_kerja,
        'Hadir Tepat Waktu': item.hadir_tepat_waktu,
        'Hadir Terlambat': item.hadir_terlambat,
        'Total Hadir': item.total_hadir,
        'Izin': item.total_izin,
        'Mangkir': item.total_mangkir,
        'Skor Kehadiran (%)': item.skor_kehadiran,
        'Skor Ketepatan (%)': item.skor_ketepatan,
        'Skor Akhir (%)': item.skor_akhir
      }));

      const wb = XLSX.utils.book_new();
      const ws = XLSX.utils.json_to_sheet(excelData);
      
      // Set column widths
      ws['!cols'] = [
        { wch: 5 }, { wch: 30 }, { wch: 20 }, { wch: 30 }, { wch: 20 }, { wch: 25 },
        { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 15 }, { wch: 12 }, { wch: 8 },
        { wch: 10 }, { wch: 15 }, { wch: 15 }, { wch: 12 }
      ];

      XLSX.utils.book_append_sheet(wb, ws, 'Laporan Instansi');
      XLSX.writeFile(wb, `Laporan_Instansi_${getMonthName(selectedMonth)}_${selectedYear}.xlsx`);
      toast.success('Excel berhasil diunduh');
    } catch (error) {
      toast.error('Gagal mengekspor Excel');
    } finally {
      setExporting(false);
    }
  };

  // Chart data
  const chartData = kinerjaAggregate ? [
    { name: 'Sangat Baik', value: kinerjaAggregate.distribusi_skor.sangat_baik, color: '#22c55e' },
    { name: 'Baik', value: kinerjaAggregate.distribusi_skor.baik, color: '#3b82f6' },
    { name: 'Cukup', value: kinerjaAggregate.distribusi_skor.cukup, color: '#f59e0b' },
    { name: 'Kurang', value: kinerjaAggregate.distribusi_skor.kurang, color: '#ef4444' }
  ] : [];

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="laporan-instansi-page">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="icon" onClick={() => navigate(-1)}>
            <ChevronLeft className="w-5 h-5" />
          </Button>
          <div>
            <h1 className="text-2xl font-bold text-indigo-950">Laporan Instansi</h1>
            <p className="text-gray-500">Laporan kinerja pegawai instansi/kantor</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={loadData} disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
          <Button variant="outline" onClick={exportToExcel} disabled={exporting || !laporan?.data?.length}>
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Excel
          </Button>
          <Button onClick={exportToPDF} disabled={exporting || !laporan?.data?.length} className="bg-indigo-950">
            <Download className="w-4 h-4 mr-2" />
            PDF
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card className="mb-6 border-indigo-100/50">
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="space-y-2">
              <label className="text-sm font-medium">Bulan</label>
              <Select value={selectedMonth.toString()} onValueChange={(v) => { setSelectedMonth(parseInt(v)); setCurrentPage(1); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {Array.from({ length: 12 }, (_, i) => (
                    <SelectItem key={i + 1} value={(i + 1).toString()}>
                      {getMonthName(i + 1)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Tahun</label>
              <Select value={selectedYear.toString()} onValueChange={(v) => { setSelectedYear(parseInt(v)); setCurrentPage(1); }}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {[2024, 2025, 2026].map(year => (
                    <SelectItem key={year} value={year.toString()}>{year}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Instansi</label>
              <Select value={selectedInstansi} onValueChange={(v) => { setSelectedInstansi(v); setCurrentPage(1); }}>
                <SelectTrigger>
                  <SelectValue placeholder="Semua Instansi" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Semua Instansi</SelectItem>
                  {instansiList.map(instansi => (
                    <SelectItem key={instansi.id} value={instansi.id}>
                      {instansi.nama}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <label className="text-sm font-medium">Cari Pegawai</label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                <Input
                  placeholder="Nama pegawai..."
                  value={searchQuery}
                  onChange={(e) => handleSearchChange(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Statistics Cards */}
      {kinerjaAggregate && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <Card className="border-indigo-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-indigo-100 rounded-lg">
                  <Users className="w-5 h-5 text-indigo-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Total Pegawai</p>
                  <p className="text-2xl font-bold text-indigo-950">{kinerjaAggregate.total_pegawai}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-indigo-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-emerald-100 rounded-lg">
                  <Award className="w-5 h-5 text-emerald-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Rata-rata Skor</p>
                  <p className="text-2xl font-bold text-emerald-600">{kinerjaAggregate.avg_skor_akhir}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-indigo-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-blue-100 rounded-lg">
                  <TrendingUp className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Rata-rata Kehadiran</p>
                  <p className="text-2xl font-bold text-blue-600">{kinerjaAggregate.avg_kehadiran}%</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="border-indigo-100/50">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="p-2 bg-amber-100 rounded-lg">
                  <Briefcase className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="text-sm text-gray-500">Hari Kerja</p>
                  <p className="text-2xl font-bold text-amber-600">{laporan?.total_hari_kerja || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Chart & Table */}
      <div className="grid lg:grid-cols-3 gap-6">
        {/* Chart */}
        <Card className="border-indigo-100/50">
          <CardHeader>
            <CardTitle className="text-lg">Distribusi Kinerja</CardTitle>
            <CardDescription>Berdasarkan kategori skor</CardDescription>
          </CardHeader>
          <CardContent>
            {kinerjaAggregate && chartData.some(d => d.value > 0) ? (
              <div className="h-[250px]" ref={chartRef}>
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={2}
                      dataKey="value"
                      label={({ name, value }) => value > 0 ? `${name}: ${value}` : ''}
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip />
                    <Legend />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            ) : (
              <div className="h-[250px] flex items-center justify-center text-gray-400">
                <p>Belum ada data</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Table */}
        <Card className="lg:col-span-2 border-indigo-100/50">
          <CardHeader>
            <CardTitle className="text-lg flex items-center gap-2">
              <FileText className="w-5 h-5" />
              Data Kinerja Pegawai
            </CardTitle>
            <CardDescription>
              {laporan?.pagination?.total || 0} pegawai ditemukan
            </CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
              </div>
            ) : !laporan?.data?.length ? (
              <div className="text-center py-12 text-gray-500">
                <Users className="w-12 h-12 mx-auto mb-3 text-gray-300" />
                <p>Tidak ada data pegawai</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>No</th>
                        <th>Pegawai</th>
                        <th>Instansi</th>
                        <th>Jabatan</th>
                        <th>Hadir</th>
                        <th>Izin</th>
                        <th>Mangkir</th>
                        <th>Skor</th>
                        <th>Aksi</th>
                      </tr>
                    </thead>
                    <tbody>
                      {laporan.data.map((item, index) => (
                        <tr key={item.pegawai_id}>
                          <td>{(currentPage - 1) * ITEMS_PER_PAGE + index + 1}</td>
                          <td>
                            <div>
                              <p className="font-medium">{item.pegawai_nama}</p>
                              <p className="text-xs text-gray-500">{item.pegawai_nip}</p>
                            </div>
                          </td>
                          <td className="text-sm">{item.instansi_nama}</td>
                          <td>
                            <div>
                              <Badge className="bg-indigo-100 text-indigo-800 text-xs">
                                {item.jabatan}
                              </Badge>
                              {item.bidang !== '-' && (
                                <p className="text-xs text-gray-500 mt-1">{item.bidang}</p>
                              )}
                            </div>
                          </td>
                          <td>
                            <div className="flex gap-1">
                              <Badge className="bg-emerald-100 text-emerald-800 text-xs">
                                {item.hadir_tepat_waktu}
                              </Badge>
                              <Badge className="bg-amber-100 text-amber-800 text-xs">
                                {item.hadir_terlambat}
                              </Badge>
                            </div>
                          </td>
                          <td>
                            <Badge className="bg-blue-100 text-blue-800 text-xs">
                              {item.total_izin}
                            </Badge>
                          </td>
                          <td>
                            <Badge className="bg-red-100 text-red-800 text-xs">
                              {item.total_mangkir}
                            </Badge>
                          </td>
                          <td>
                            <Badge className={getScoreColor(item.skor_akhir)}>
                              {item.skor_akhir}%
                            </Badge>
                          </td>
                          <td>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openDetailDialog(item)}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-4 pt-4 border-t">
                    <p className="text-sm text-gray-500">
                      Halaman {currentPage} dari {totalPages}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button variant="outline" size="sm" onClick={() => goToPage(1)} disabled={currentPage === 1}>
                        <ChevronsLeft className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => goToPage(currentPage - 1)} disabled={currentPage === 1}>
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      <span className="px-3 py-1 text-sm">{currentPage}</span>
                      <Button variant="outline" size="sm" onClick={() => goToPage(currentPage + 1)} disabled={currentPage === totalPages}>
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                      <Button variant="outline" size="sm" onClick={() => goToPage(totalPages)} disabled={currentPage === totalPages}>
                        <ChevronsRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Detail Dialog */}
      <Dialog open={showDetailDialog} onOpenChange={setShowDetailDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCircle className="w-5 h-5 text-indigo-600" />
              Detail Kehadiran Pegawai
            </DialogTitle>
          </DialogHeader>

          {loadingDetail ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
            </div>
          ) : pegawaiDetail ? (
            <div className="space-y-6">
              {/* Pegawai Info */}
              <div className="bg-indigo-50 p-4 rounded-lg">
                <h3 className="font-semibold text-indigo-950 mb-2">{pegawaiDetail.pegawai.nama}</h3>
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <div><span className="text-gray-500">NIP:</span> {pegawaiDetail.pegawai.nip}</div>
                  <div><span className="text-gray-500">Jabatan:</span> {pegawaiDetail.pegawai.jabatan}</div>
                  <div><span className="text-gray-500">Bidang:</span> {pegawaiDetail.pegawai.bidang}</div>
                  <div><span className="text-gray-500">Instansi:</span> {pegawaiDetail.instansi.nama}</div>
                  <div><span className="text-gray-500">Jam Kerja:</span> {pegawaiDetail.instansi.jam_masuk} - {pegawaiDetail.instansi.jam_pulang}</div>
                  <div><span className="text-gray-500">Hari Kerja:</span> {pegawaiDetail.total_hari_kerja} hari</div>
                </div>
              </div>

              {/* Daily Records */}
              <div>
                <h4 className="font-medium mb-3">Rekap Harian</h4>
                {Object.keys(pegawaiDetail.daily_records).length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="data-table text-sm">
                      <thead>
                        <tr>
                          <th>Tanggal</th>
                          <th>Masuk</th>
                          <th>Status</th>
                          <th>Pulang</th>
                        </tr>
                      </thead>
                      <tbody>
                        {Object.entries(pegawaiDetail.daily_records)
                          .sort(([a], [b]) => a.localeCompare(b))
                          .map(([date, record]) => (
                            <tr key={date}>
                              <td>{new Date(date).toLocaleDateString('id-ID', { weekday: 'short', day: 'numeric', month: 'short' })}</td>
                              <td>{record.masuk?.waktu || '-'}</td>
                              <td>
                                {record.masuk?.status === 'hadir_tepat_waktu' && (
                                  <Badge className="bg-emerald-100 text-emerald-800 text-xs">Tepat</Badge>
                                )}
                                {record.masuk?.status === 'hadir_terlambat' && (
                                  <Badge className="bg-amber-100 text-amber-800 text-xs">Terlambat</Badge>
                                )}
                                {!record.masuk && <span className="text-gray-400">-</span>}
                              </td>
                              <td>{record.pulang?.waktu || '-'}</td>
                            </tr>
                          ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <p className="text-gray-500 text-center py-4">Belum ada data kehadiran</p>
                )}
              </div>

              {/* Leave Records */}
              {pegawaiDetail.izin_list?.length > 0 && (
                <div>
                  <h4 className="font-medium mb-3">Riwayat Izin</h4>
                  <div className="space-y-2">
                    {pegawaiDetail.izin_list.map((izin, idx) => (
                      <div key={idx} className="p-3 bg-blue-50 rounded-lg text-sm">
                        <div className="flex justify-between">
                          <span className="font-medium">{izin.jenis_izin}</span>
                          <Badge className="bg-blue-100 text-blue-800 text-xs">Disetujui</Badge>
                        </div>
                        <p className="text-gray-600 text-xs mt-1">
                          {new Date(izin.tanggal_mulai).toLocaleDateString('id-ID')} - {new Date(izin.tanggal_selesai).toLocaleDateString('id-ID')}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default LaporanInstansiPage;
